import { create } from 'zustand';
import type { StepId, StepState, RepairLogEntry } from './reconciliation-types';
import type { FullDiagnosticReport } from '../invoice-repair/types';

interface ReconciliationState {
  // Period selection
  dateFrom: string;
  dateTo: string;
  storeId: string | undefined;
  monthLabel: string;

  // Wizard state
  activeStep: StepId;
  steps: StepState[];

  // Data
  diagnostic: FullDiagnosticReport | null;
  isLoading: boolean;
  error: string | null;

  // Repair log
  repairLog: RepairLogEntry[];

  // Operation lock
  isExecuting: boolean;

  // Actions
  setDateRange: (dateFrom: string, dateTo: string, label: string) => void;
  setStoreId: (storeId: string | undefined) => void;
  setActiveStep: (step: StepId) => void;
  setDiagnostic: (data: FullDiagnosticReport | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  updateStepStatus: (stepId: StepId, status: StepState['status'], summary?: string) => void;
  addRepairLog: (entry: Omit<RepairLogEntry, 'id' | 'timestamp'>) => void;
  setIsExecuting: (executing: boolean) => void;
  reset: () => void;
}

const defaultSteps: StepState[] = [
  { id: 'sync', label: 'Synchronizacja', status: 'idle' },
  { id: 'missing', label: 'Brakujące faktury', status: 'idle' },
  { id: 'numbering', label: 'Numeracja', status: 'idle' },
  { id: 'verification', label: 'Weryfikacja', status: 'idle' },
];

// Get current month defaults
const now = new Date();
const defaultDateFrom = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
const defaultDateTo = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
const defaultMonthLabel = now.toLocaleDateString('pl-PL', { month: 'long', year: 'numeric' });

export const useReconciliation = create<ReconciliationState>((set) => ({
  dateFrom: defaultDateFrom,
  dateTo: defaultDateTo,
  storeId: undefined,
  monthLabel: defaultMonthLabel,
  activeStep: 'sync',
  steps: [...defaultSteps],
  diagnostic: null,
  isLoading: false,
  error: null,
  repairLog: [],
  isExecuting: false,

  setDateRange: (dateFrom, dateTo, label) => set({ dateFrom, dateTo, monthLabel: label, diagnostic: null, steps: defaultSteps.map(s => ({ ...s })) }),
  setStoreId: (storeId) => set({ storeId, diagnostic: null, steps: defaultSteps.map(s => ({ ...s })) }),
  setActiveStep: (step) => set({ activeStep: step }),
  setDiagnostic: (data) => set({ diagnostic: data }),
  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),
  updateStepStatus: (stepId, status, summary) => set((state) => ({
    steps: state.steps.map(s => s.id === stepId ? { ...s, status, summary } : s),
  })),
  addRepairLog: (entry) => set((state) => ({
    repairLog: [...state.repairLog, { ...entry, id: crypto.randomUUID(), timestamp: new Date() }],
  })),
  setIsExecuting: (executing) => set({ isExecuting: executing }),
  reset: () => set({
    diagnostic: null,
    isLoading: false,
    error: null,
    repairLog: [],
    isExecuting: false,
    steps: defaultSteps.map(s => ({ ...s })),
    activeStep: 'sync',
  }),
}));
